<template>
  <div id="layout-template-storage-position" v-if="showStoragePosition()">
    <el-row class="default-section-header">
      <el-col
        class="default-section-title"
      >{{ $t('layoutTemplate.storagePositionTitle') }}</el-col>
    </el-row>

    <el-row id="layout-template-storage-position-message" :gutter="10" v-show="!existsSelectedStorageLocation">
      <el-col>
        <span>{{ $t('layoutTemplate.selectStorageLocationMessage') }}</span>
      </el-col>
    </el-row>

    <div id="layout-template-storage-position-details" v-show="existsSelectedStorageLocation">
      <el-row :gutter="10" class="storage-details-margin-bottom">
        <el-col :span="6">
          <label class="margin-bottom-small-list">{{ $t('layoutTemplate.storageLocation') }}</label>
        </el-col>
        <el-col :span="2">
          <span class="span-small-list">{{ storageLocationCode }}</span>
        </el-col>
        <el-col :span="2">
          <label class="margin-bottom-small-list">{{ $t('layoutTemplate.area') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list">{{ area }}</span>
        </el-col>
        <el-col :span="6">
          <label class="margin-bottom-small-list">{{ $t('layoutTemplate.shipperGroup') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list">{{ shipperGroupName }}</span>
        </el-col>
      </el-row>

      <el-row :gutter="10">
        <el-col :span="6">
          <label class="margin-bottom-small-list">{{ $t('layoutTemplate.positionsAssigned') }}</label>
        </el-col>
        <el-col :span="2">
          <span class="span-small-list">{{ positionsAssigned }}</span>
        </el-col>
        <el-col class="text-align-end" :span="16">
          <add-storage-position
            :layoutTemplateId="layoutTemplateId"
            :storageLocationId="searchForm.storageLocationId"
            :layoutType="layoutType"
            :layoutTemplateName="layoutTemplateName"
            @searchStoragePositions="handleSearch()" />
        </el-col>
      </el-row>

      <el-row :gutter="10">
        <el-col>
          <el-form :model="editForm" :rules="editFormRules" ref="editForm">
            <el-table
              :data="storagePosition.list"
              v-loading="storagePosition.loadingSearch"
              size="mini"
              tooltip-effect="dark"
              class="dark-blue-table"
              :empty-text="$t('layoutTemplate.noPositionsMessage')"
              stripe
            >
              <el-table-column
                prop="storageLocation"
                :label="$t('layoutTemplate.position')"
                :min-width="5"
                align="center"
              >
                <template slot-scope="scope">
                  <template v-if="scope.row.edit">
                    <el-form-item prop="positionNumber">
                      <el-input v-model="editForm.positionNumber" v-alphanumeric-validation maxlength="50"></el-input>
                    </el-form-item>
                  </template>
                  <template v-else>
                    <span>{{ scope.row.positionNumber }}</span>
                  </template>
                </template>
              </el-table-column>

              <el-table-column
                prop="consignee"
                :label="$t('layoutTemplate.consignee')"
                :min-width="5"
                align="left"
                header-align="center"
              >
                <template slot-scope="scope">
                  <template v-if="scope.row.edit">
                    <el-form-item prop="accountId">
                      <autocomplete :model.sync="editForm.accountId"
                        :shipperAccountId="null"
                        labelFieldLastWithDash="number"
                        popperAppendToBody
                        :url="urlConsignee"
                        labelField="name"
                        valueField="id"
                        id="rows-consignee"
                        ref="rows-consignee" />
                    </el-form-item>
                  </template>
                  <template v-else>
                    <el-tooltip class="item" effect="dark" :content="`${ scope.row.consigneeNameAccount }`" placement="top">
                      <span> {{ scope.row.consigneeNameAccount }}</span>
                    </el-tooltip>
                  </template>
                </template>
              </el-table-column>

              <el-table-column prop="options" :label="$t('common.options')" :min-width="5" align="center">
                <template slot-scope="scope">
                  <el-tooltip
                          v-if="showEditButton(scope.row)"
                          class="item"
                          effect="dark"
                          :enterable="false"
                          :content="$t('warehouse.orderEntry.edit')"
                          placement="top-start"
                  >
                    <i class="el-icon-edit icon-action" @click="handleEdit(scope.row)"></i>
                  </el-tooltip>

                  <el-tooltip
                          v-if="showEditButton(scope.row)"
                          class="item"
                          effect="dark"
                          :enterable="false"
                          :content="$t('warehouse.orderEntry.delete')"
                          placement="top-start"
                  >
                    <i class="el-icon-delete icon-action" @click="handleDelete(scope.row)"></i>
                  </el-tooltip>
                  <template v-else-if="scope.row.edit">
                    <el-button icon="el-icon-success" circle size="mini" @click="handleUpdate(scope.row)"></el-button>
                    <el-button icon="el-icon-circle-close" circle size="mini" @click="cancelEdit(scope.row)"></el-button>
                  </template>
                </template>
              </el-table-column>
            </el-table>
          </el-form>
          <div class="pagination-footer" v-show="storagePosition.total > 0">
            <span class="el-pagination__total" slot>
              <span class="el-pagination__title">{{ $t('common.total') }}</span>
              {{ storagePosition.total }}
            </span>
            <el-pagination
              @size-change="handleSearchChangeLimit"
              :page-sizes="sizes"
              :page-size="storagePosition.actualFilters.rows"
              :current-page.sync="storagePosition.actualFilters.page"
              @current-change="handleSearchChangePage"
              :total="storagePosition.total"
              layout="slot, sizes, prev, pager, next, jumper"
            ></el-pagination>
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Message } from 'element-ui';
import constants from '@/utils/constants';
import Autocomplete from '@/components/Autocomplete';
import apiConstants from '@/utils/apiConstants';
import AddStoragePosition from './AddStoragePosition';

const storagePositionSetEditingAction = 'storagePosition/setEditing';
const rowsConsigneeReference = 'rows-consignee';

export default {
  name: 'StoragePosition',
  components: { Autocomplete, AddStoragePosition },
  props: {
    layoutTemplateId: {
      type: Number,
      required: true
    },
    storageLocationId: {
      type: Number,
      required: true
    },
    storageLocationCode: {
      type: String,
      default: ''
    },
    area: {
      type: String,
      default: ''
    },
    shipperGroupName: {
      type: String,
      default: ''
    },
    layoutType: {
      type: Number,
      required: true
    },
    layoutTemplateName: {
      type: String,
      default: ''
    }
  },
  watch: {
    storageLocationId(newValue) {
      this.searchForm.storageLocationId = newValue;
      this.clearStoragePosition();
      this.handleSearch();
    }
  },
  computed: {
    ...mapGetters(['storagePosition', 'storageLocation']),
    existsSelectedStorageLocation() {
      return (
        this.searchForm.storageLocationId
        && this.searchForm.storageLocationId > 0
      );
    },
    positionsAssigned() {
      const selectedLocation = this.storageLocation.list.find(location => location.id === this.storageLocationId);
      if (selectedLocation) {
        return selectedLocation.positionsAssigned;
      }
      return '0/0';
    }
  },
  mounted() {
    this.clearStoragePosition();
    this.handleSearch();
  },
  data() {
    return {
      editForm: {
        id: null,
        storageLocationId: null,
        positionNumber: null,
        accountId: 0
      },
      editFormRules: {
        positionNumber: [
          {
            required: true,
            message: this.$t('layoutTemplate.error.positionRequired'),
            trigger: 'blur'
          }
        ]
      },
      searchForm: {
        storageLocationId: this.storageLocationId
      },
      sizes: constants.TABLES.DEFAULT_LIMIT_SIZES,
      urlConsignee: apiConstants.END_POINTS.ACCOUNTS.CONSIGNEE,
    };
  },
  methods: {
    async handleSearch() {
      if (this.existsSelectedStorageLocation) {
        await this.$store.dispatch(
          'storagePosition/search',
          this.searchForm
        );
      }
    },
    handleSearchChangePage(val) {
      Object.assign(this.searchForm, { page: val });
      this.handleSearch();
    },
    handleSearchChangeLimit(val) {
      Object.assign(this.searchForm, {
        rows: val,
        page: constants.TABLES.DEFAULT_PAGE
      });
      this.handleSearch();
    },
    showStoragePosition() {
      return (this.layoutType === constants.LAYOUT_TEMPLATE.LAYOUT_TYPES.TEMPLATE && this.$can(this.permissions.WAREHOUSE.VIEW_LAYOUT_TEMPLATES))
          || (this.layoutType === constants.LAYOUT_TEMPLATE.LAYOUT_TYPES.LAYOUT && this.$can(this.permissions.WAREHOUSE.VIEW_LAYOUTS));
    },
    clearStoragePosition() {
      this.$store.dispatch('storagePosition/clear');
    },
    handleDelete(row) {
      const message = this.$t('layoutTemplate.deletePositionConfirmMessage')
        .replace('[locationCode]', this.storageLocationCode)
        .replace('[area]', this.area)
        .replace('[positionNumber]', row.positionNumber);
      this.$confirm(message, { dangerouslyUseHTMLString: true, confirmButtonText: this.$t('common.yes') })
        .then(() => {
          this.deletePosition(row);
        }).catch(() => {});
    },
    handleEdit(row) {
      this.$store.dispatch(storagePositionSetEditingAction, true);
      row.edit = true;
      this.editForm.id = row.id;
      this.editForm.storageLocationId = row.storageLocationId;
      this.editForm.positionNumber = row.positionNumber;
      this.editForm.accountId = row.accountId;
      this.$nextTick(() => {
        if (row.consigneeNameAccount) {
          this.$refs[rowsConsigneeReference].setLocalModel(`${row.consigneeNameAccount}`);
        }
        if (this.$refs[rowsConsigneeReference].$el) {
          this.$refs[rowsConsigneeReference].$el.getElementsByTagName('input')[0].focus();
        }
      });
    },
    cancelEdit(row) {
      this.$store.dispatch(storagePositionSetEditingAction, false);
      row.edit = false;
    },
    handleUpdate() {
      this.$refs.editForm.validate((valid, validationsErrors) => {
        if (valid) {
          Message.closeAll();
          const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
          this.$store
            .dispatch('storagePosition/update', { positionToUpdate: this.editForm, positionId: this.editForm.id })
            .then((response) => {
              this.processSuccessDeleteOrUpdate(response, loading);
            })
            .catch(() => {
              loading.close();
            });
        } else {
          let errorMessage = '';
          Object.keys(validationsErrors).forEach((key) => {
            validationsErrors[key].forEach((validation) => {
              errorMessage += `${validation.message}. `;
            });
          });
          this.$message.warning(errorMessage);
        }
      });
    },
    deletePosition(row) {
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      this.$store.dispatch('storagePosition/delete', row.id).then((response) => {
        this.processSuccessDeleteOrUpdate(response, loading);
      })
        .catch(() => {
          loading.close();
        });
    },
    processSuccessDeleteOrUpdate(response, loading) {
      loading.close();
      this.handleSearch();
      this.$store.dispatch('storageLocation/search', {});
      this.$store.dispatch(storagePositionSetEditingAction, false);
      this.$message.success(response.message);
    },
    showEditButton(row) {
      return (
        ((this.layoutType === constants.LAYOUT_TEMPLATE.LAYOUT_TYPES.TEMPLATE && this.$can(this.permissions.WAREHOUSE.EDIT_LAYOUT_TEMPLATES))
            || (this.layoutType === constants.LAYOUT_TEMPLATE.LAYOUT_TYPES.LAYOUT && this.$can(this.permissions.WAREHOUSE.EDIT_LAYOUTS)))
        && !this.storagePosition.isEditing
        && !row.edit
      );
    }
  }
};
</script>

<style rel="stylesheet/scss" lang="scss">
div#layout-template-storage-position {
  .default-section-title {
    font-size: 18px;
    padding-left: 0px;
    text-align: left;
  }

  .el-table__empty-text {
    width: 100%;
  }

  th {
    line-height: 1.15;
    .cell {
      line-height: 16px;
      height: 16px;
    }
  }

  .storage-details-margin-bottom {
    margin-bottom: 18px;
  }

  .add-position-margin-bottom {
    margin-bottom: 10px;
  }
}
</style>
